import Button from 'antd/lib/button';
import Icon from 'antd/lib/icon';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import Spin from 'antd/lib/spin';
import * as React from 'react';
import {
  ADDITIONAL_CHARGES_LABELS,
  ADDITIONAL_CHARGES_MESSAGES,
  CHARGE_TYPE_LABELS,
} from './AdditionalCharges.constants';
import { AdditionalChargeMasterOption, ChargeRow } from './AdditionalCharges.types';

export interface AdditionalChargeDeclarationHtmlProps {
  classes: Record<string, string>;
  isLoading: boolean;
  errorMessage?: string;
  isReadOnly: boolean;
  vendorType?: string;
  options: AdditionalChargeMasterOption[];
  rows: ChargeRow[];
  /** Codes already chosen in another row — disabled so the same charge cannot be picked twice. */
  selectedCodes: string[];
  onSelectCharge: (rowId: string, chargeCode?: string) => void;
  onAddRow: () => void;
  onRemoveRow: (rowId: string) => void;
  getOptionByCode: (chargeCode?: string) => AdditionalChargeMasterOption | undefined;
  /** Declares the selected charges and flips the field to Part 2 on success. */
  onNext: () => void;
  isDeclaring: boolean;
  /** `taskId` didn't make it onto this field's extraData — Next can't call `/declare` without it. */
  showMissingTaskId: boolean;
}

/**
 * Part 1 of the Additional Vendor Charges task — the vendor declares which charges they incurred.
 *
 * Presentational only. The "Type of charge" beside each picker is derived from the selected option's
 * `chargeType` and rendered disabled: it is a property of the charge, never an input.
 */
export const AdditionalChargeDeclarationHtml = ({
  classes,
  isLoading,
  errorMessage,
  isReadOnly,
  vendorType,
  options,
  rows,
  selectedCodes,
  onSelectCharge,
  onAddRow,
  onRemoveRow,
  getOptionByCode,
  onNext,
  isDeclaring,
  showMissingTaskId,
}: AdditionalChargeDeclarationHtmlProps) => {
  if (!vendorType) {
    return <div className={classes.errorMessage}>{ADDITIONAL_CHARGES_MESSAGES.missingVendorType}</div>;
  }

  if (isLoading) {
    return (
      <div className={classes.spinner}>
        <Spin />
      </div>
    );
  }

  if (errorMessage) {
    return <div className={classes.errorMessage}>{errorMessage}</div>;
  }

  const renderTypeOfCharge = (chargeCode?: string) => {
    const chargeType = getOptionByCode(chargeCode)?.chargeType;
    return (
      <div className={classes.readOnlyValue}>
        <Input disabled value={chargeType ? CHARGE_TYPE_LABELS[chargeType] : ''} />
      </div>
    );
  };

  /**
   * A completed task still has to show what was declared, so the catalog is read even when the field
   * is read-only. A charge deactivated since declaration is absent from the catalog — fall back to its
   * raw code rather than rendering an empty cell.
   */
  if (isReadOnly) {
    const declaredCodes = rows.map((row) => row.chargeCode).filter(Boolean) as string[];

    if (!declaredCodes.length) {
      return <div className={classes.message}>{ADDITIONAL_CHARGES_MESSAGES.noChargesDeclared}</div>;
    }

    return (
      <div className={classes.container}>
        {declaredCodes.map((chargeCode) => {
          const option = getOptionByCode(chargeCode);
          return (
            <div className={classes.summaryRow} key={chargeCode}>
              <div className={classes.summaryValue}>{option?.chargeName || chargeCode}</div>
              <div className={classes.summaryValue}>
                {option ? CHARGE_TYPE_LABELS[option.chargeType] : ''}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Without a task reference the task-scoped catalog cannot be loaded and nothing can be declared, so
  // surface the wiring gap directly rather than a misleading "no charges configured" empty state.
  if (showMissingTaskId) {
    return <div className={classes.errorMessage}>{ADDITIONAL_CHARGES_MESSAGES.missingTaskId}</div>;
  }

  if (!options.length) {
    return <div className={classes.message}>{ADDITIONAL_CHARGES_MESSAGES.emptyMaster(vendorType)}</div>;
  }

  const showRemove = rows.length > 1;

  return (
    <div className={classes.container}>
      {/* Single column headers, once — the rows below carry only controls, so everything lines up. */}
      <div className={showRemove ? classes.rowWithRemove : classes.row}>
        <span className={classes.label}>{ADDITIONAL_CHARGES_LABELS.chargeRow}</span>
        <span className={classes.label}>{ADDITIONAL_CHARGES_LABELS.chargeType}</span>
        {showRemove && <span />}
      </div>

      {rows.map((row) => (
        <div className={showRemove ? classes.rowWithRemove : classes.row} key={row.rowId}>
          <Select
            allowClear
            showSearch
            optionFilterProp="children"
            className={classes.select}
            dropdownMatchSelectWidth={false}
            placeholder={ADDITIONAL_CHARGES_LABELS.chargePlaceholder}
            value={row.chargeCode}
            onChange={(value) => onSelectCharge(row.rowId, value as string | undefined)}
          >
            {options.map((option) => (
              <Select.Option
                key={option.chargeCode}
                value={option.chargeCode}
                disabled={
                  option.chargeCode !== row.chargeCode && selectedCodes.includes(option.chargeCode)
                }
              >
                {option.chargeName}
              </Select.Option>
            ))}
          </Select>

          {renderTypeOfCharge(row.chargeCode)}

          {showRemove && (
            <Icon
              type="close"
              className={classes.removeIcon}
              role="button"
              aria-label={ADDITIONAL_CHARGES_LABELS.removeRow}
              onClick={() => onRemoveRow(row.rowId)}
            />
          )}
        </div>
      ))}

      <div className={classes.addRow} role="button" onClick={onAddRow}>
        <Icon type="plus" />
        <span>{ADDITIONAL_CHARGES_LABELS.addRow}</span>
      </div>

      <div className={classes.nextRow}>
        <Button
          type="primary"
          disabled={!selectedCodes.length}
          loading={isDeclaring}
          onClick={onNext}
        >
          {ADDITIONAL_CHARGES_LABELS.next}
        </Button>
      </div>
    </div>
  );
};

export default AdditionalChargeDeclarationHtml;
